import React, { useEffect, useRef, useState } from "react";
import { getUserProfile, resolveUserProfile, UserProfile } from "../auth/googleAuth";
import { useFieldworkerMode } from "../utils/fieldworkerMode";
import { useT } from "../i18n";

interface UserMenuProps {
  onSignOut: () => void;
}

// Google-style account avatar at the right of the NavBar. Shows the user's profile picture
// (or their initial as a fallback), and a popover with name/email/tier + sign out. All data
// comes from the non-sensitive openid/email/profile scopes via the userinfo endpoint.
export default function UserMenu({ onSignOut }: UserMenuProps) {
  const t = useT();
  const fieldworker = useFieldworkerMode();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile>(getUserProfile);
  const [imgError, setImgError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const p = await resolveUserProfile();
      if (alive) setProfile(p);
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const email = profile.email || "";
  const name = profile.name || "";
  const picture = profile.picture || "";
  const initial = (name || email || "?").trim().charAt(0).toUpperCase();
  const showPicture = !!picture && !imgError;
  const roleLabel = fieldworker ? t("nav.fieldworker") : t("account.manager");

  const avatar = (sizeCls: string) =>
    showPicture ? (
      <img
        src={picture}
        alt={name || email}
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
        className={`${sizeCls} rounded-full object-cover`}
      />
    ) : (
      <span className={`${sizeCls} rounded-full bg-pink-600 text-white inline-flex items-center justify-center font-semibold select-none`}>
        {initial}
      </span>
    );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-pink-300 ring-offset-1"
        title={t("account.menu")}
        aria-label={t("account.menu")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {avatar("h-8 w-8 text-sm")}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-20"
        >
          <div className="px-4 py-2 flex items-center gap-3">
            {avatar("h-10 w-10 text-base shrink-0")}
            <div className="min-w-0">
              {name && <p className="text-sm font-medium text-gray-800 truncate">{name}</p>}
              <p className="text-xs text-gray-500 truncate" title={email}>{email}</p>
            </div>
          </div>
          <div className="px-4 pb-2">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2 py-1 border ${
              fieldworker
                ? "text-pink-700 bg-pink-50 border-pink-200"
                : "text-indigo-700 bg-indigo-50 border-indigo-200"}`}>
              <i className={`fa-solid ${fieldworker ? "fa-people-group" : "fa-user-shield"}`} aria-hidden="true" />
              {roleLabel}
            </span>
          </div>
          <div className="border-t border-gray-100 my-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSignOut(); }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 inline-flex items-center gap-2"
          >
            <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
            <span>{t("app.signOut")}</span>
          </button>
        </div>
      )}
    </div>
  );
}
